/**
 * 承認ルート(申請 → 承認 ×N → タスク起票)を `Steps` の項目にする。
 * 承認待ちの右パネル・申請詳細の履歴・新しい申請の右カラムで同じ形を使う。
 */
import type { WfDecision, WfRequest } from "../../lib/api";
import { jst } from "../../lib/format";
import type { StepItem } from "../../ui";

type RouteSource = Pick<
  WfRequest,
  "status" | "required" | "approved_count" | "approved_by" | "requested_by" | "created_at" | "decided_by" | "decided_at" | "reason" | "task_total" | "task_done"
> & { decisions?: WfDecision[] };

/** 一覧の行(decisions 無し)でも、詳細(decisions 有り)でも組める。 */
export function approvalRoute(req: RouteSource): StepItem[] {
  const approvals = (req.decisions ?? [])
    .filter((d) => d.decision === "approve")
    .map((d) => ({ by: d.decided_by, at: d.created_at }));
  const approvedBy =
    approvals.length > 0 ? approvals : req.approved_by.map((by) => ({ by, at: null as string | null }));
  const reject = (req.decisions ?? []).find((d) => d.decision === "reject");
  const rejectedBy = req.status === "rejected" ? { by: reject?.decided_by ?? req.decided_by, at: reject?.created_at ?? req.decided_at, reason: reject?.reason ?? req.reason } : null;
  // 取り下げは申請者側の操作。承認票の行は無いので申請の decided_* から出す
  const withdrawnBy = req.status === "withdrawn" ? { by: req.decided_by, at: req.decided_at, reason: req.reason } : null;

  const items: StepItem[] = [
    {
      key: "request",
      label: "申請",
      sub: [req.requested_by, req.created_at ? jst(req.created_at) : ""].filter(Boolean).join(" · "),
      state: "done",
    },
  ];
  const required = Math.max(req.required, approvedBy.length, 1);
  for (let i = 0; i < required; i++) {
    const a = approvedBy[i];
    const label = required > 1 ? `承認 ${i + 1} / ${required}` : "承認";
    if (a) {
      items.push({ key: `approve-${i}`, label, sub: [a.by, a.at ? jst(a.at) : ""].filter(Boolean).join(" · "), state: "done" });
    } else if (rejectedBy && i === approvedBy.length) {
      items.push({
        key: `approve-${i}`,
        label: "却下",
        sub: [rejectedBy.by, rejectedBy.at ? jst(rejectedBy.at) : "", rejectedBy.reason].filter(Boolean).join(" · "),
        state: "rejected",
      });
    } else if (withdrawnBy && i === approvedBy.length) {
      items.push({
        key: `approve-${i}`,
        label: "取り下げ",
        sub: [withdrawnBy.by, withdrawnBy.at ? jst(withdrawnBy.at) : "", withdrawnBy.reason].filter(Boolean).join(" · "),
        state: "rejected",
      });
    } else if (req.status === "pending" && i === approvedBy.length) {
      items.push({ key: `approve-${i}`, label, sub: "承認待ち", state: "current" });
    } else {
      items.push({ key: `approve-${i}`, label, state: "todo" });
    }
  }
  items.push({
    key: "tasks",
    label: "タスク起票",
    sub:
      req.status === "approved"
        ? `${req.task_done} / ${req.task_total} 完了`
        : req.status === "rejected"
          ? "却下のため起票しない"
          : req.status === "withdrawn"
            ? "取り下げのため起票しない"
            : "承認が揃うと起票する",
    state: req.status === "approved" ? "done" : "todo",
  });
  return items;
}

/** まだ出していない申請のルート(新しい申請の右カラム)。 */
export function draftRoute(required: number, requestedBy: string): StepItem[] {
  const items: StepItem[] = [{ key: "request", label: "申請", sub: requestedBy || "いまから出す", state: "current" }];
  for (let i = 0; i < Math.max(required, 1); i++) {
    items.push({ key: `approve-${i}`, label: required > 1 ? `承認 ${i + 1} / ${required}` : "承認", sub: "hr:approver", state: "todo" });
  }
  items.push({ key: "tasks", label: "タスク起票", sub: "承認が揃うと起票する", state: "todo" });
  return items;
}
